import React, { useEffect, useRef } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Link2, Heading2, Heading3, Quote, Eraser } from "lucide-react";

const cmd = (name, value) => {
  document.execCommand(name, false, value);
};

const TOOLS = [
  { id: "bold", icon: Bold, title: "Bold", run: () => cmd("bold") },
  { id: "italic", icon: Italic, title: "Italic", run: () => cmd("italic") },
  { id: "underline", icon: Underline, title: "Underline", run: () => cmd("underline") },
  { id: "h2", icon: Heading2, title: "Heading", run: () => cmd("formatBlock", "<h2>") },
  { id: "h3", icon: Heading3, title: "Sub-heading", run: () => cmd("formatBlock", "<h3>") },
  { id: "ul", icon: List, title: "Bullet list", run: () => cmd("insertUnorderedList") },
  { id: "ol", icon: ListOrdered, title: "Numbered list", run: () => cmd("insertOrderedList") },
  { id: "quote", icon: Quote, title: "Quote", run: () => cmd("formatBlock", "<blockquote>") },
  {
    id: "link", icon: Link2, title: "Link",
    run: () => {
      const url = window.prompt("Link to (https://… or mailto:…)");
      if (url) cmd("createLink", url);
    },
  },
  { id: "clear", icon: Eraser, title: "Clear formatting", run: () => cmd("removeFormat") },
];

// A small contenteditable editor: enough formatting for a policy page, no heavy dependency.
export const RichText = ({ value, onChange, testid = "rich-text", minHeight = 340 }) => {
  const ref = useRef(null);
  const mine = useRef(null);

  // Only overwrite the box when the new value came from outside (a load, a page switch, a revert) —
  // never while the caret is in it, which would eat what is being typed.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const next = value || "";
    if (next === mine.current || next === el.innerHTML) return;
    el.innerHTML = next;
  }, [value]);

  const sync = () => {
    mine.current = ref.current?.innerHTML || "";
    onChange(mine.current);
  };

  return (
    <div className="border border-border rounded-xl overflow-hidden" data-testid={`${testid}-wrap`}>
      <div className="flex flex-wrap items-center gap-1 bg-secondary px-2 py-1.5 border-b border-border">
        {TOOLS.map(({ id, icon: Icon, title, run }) => (
          <button key={id} type="button" title={title} data-testid={`${testid}-${id}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { run(); sync(); ref.current?.focus(); }}
            className="h-8 w-8 grid place-items-center rounded-lg text-foreground hover:bg-[#16a34a]/15 transition-colors">
            <Icon size={15} />
          </button>
        ))}
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning data-testid={testid}
        onInput={sync} onBlur={sync}
        style={{ minHeight }}
        className="lp-rich px-4 py-3 text-sm text-foreground outline-none overflow-y-auto max-h-[60vh]" />
    </div>
  );
};

export default RichText;
